/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, useState } from 'react';
import { useCampaignManager } from '../../communication/CampaignContext';
import { ProgrammableSurfacesCommunication } from '../../communication/ProgrammableSurfacesCommunication';
import { ProgrammableSurfacesEvents } from '../../communication/types';
import { logger } from '../../shared';
import {
    CampaignConfig,
    CampaignStateResponsePayload,
    isCampaignStatePayload
} from '../../types/CampaignTypes';
import { LftCountdownAnalystActionCard } from './components/actions/LftCountdownAnalystActionCard';
import { LftCountdownBrainstormActionCard } from './components/actions/LftCountdownBrainstormActionCard';
import { LftCountdownResearcherActionCard } from './components/actions/LftCountdownResearcherActionCard';
import { LftCountdownTryCopilotActionCard } from './components/actions/LftCountdownTryCopilotActionCard';
import { LftCountdownCard } from './components/LftCountdownCard';
import { LftCountdownActionId, useLftCountdownStyles } from './components/shared';

/**
 * Campaign configuration for trial countdown campaign.
 * Defines the 4 required actions and maps Win32 schema IDs to ActionId enum.
 */
const TRIAL_CAMPAIGN_CONFIG: CampaignConfig<LftCountdownActionId> = {
    componentId: 'trial-countdown',
    requiredActions: [
        LftCountdownActionId.ResearcherDialog,
        LftCountdownActionId.BrainStormDialog,
        LftCountdownActionId.AnalystDialog,
        LftCountdownActionId.TryCopilotDialog
    ],
    /**
     * Maps action ID strings from Win32 schema to ActionId enum values.
     * Performs case-insensitive matching to handle mixed casing from Win32.
     */
    actionMapper: (schemaActionId: string): LftCountdownActionId | null => {
        const normalized = schemaActionId.toLowerCase().trim();

        switch (normalized) {
            case 'researcherdialog':
                return LftCountdownActionId.ResearcherDialog;
            case 'brainstormingdialog':
            case 'brainstormdialog':
                return LftCountdownActionId.BrainStormDialog;
            case 'analystdialog':
                return LftCountdownActionId.AnalystDialog;
            case 'trycopilotdialog':
                return LftCountdownActionId.TryCopilotDialog;
            default:
                // Unknown action IDs are ignored with warning
                console.warn(`[LftSidePaneTrialCountdownCard] Unknown action ID ignored: ${schemaActionId}`);
                return null;
        }
    }
};

export const LftTrialCountdownExperiment = () => {
    const styles = useLftCountdownStyles();

    // Campaign state: tracks which actions are completed
    const [campaignState, setCampaignState] = useState<Set<LftCountdownActionId>>(new Set());
    const [campaignStateReceived, setCampaignStateReceived] = useState<boolean>(false);

    // Get campaign manager from React Context
    const campaignManager = useCampaignManager(TRIAL_CAMPAIGN_CONFIG);

    const handleActionClick = (actionId: LftCountdownActionId, logTag: string, logMessage: string) => {
        logger.logInfo(logTag, logMessage);

        if (!campaignState.has(actionId)) {
            const newState = new Set(campaignState);
            newState.add(actionId);
            setCampaignState(newState);
            campaignManager.completeAction(actionId);
        }
    };

    // Campaign state initialization - Request-Response pattern
    useEffect(() => {
        const communication = ProgrammableSurfacesCommunication.getInstance();

        // Handler for campaign state response from Win32
        const handleCampaignStateResponse = (payload: CampaignStateResponsePayload) => {
            try {
                // Validate incoming data
                if (!isCampaignStatePayload(payload)) {
                    console.error('[LftSidePaneTrialCountdownCard] Invalid CampaignStatePayload received:', payload);
                    return;
                }

                logger.logInfo('CampaignStateResponse', 'Campaign state response received from Win32');

                // Initialize campaign state in manager
                campaignManager.initializeCampaignState(payload);

                // Extract completed actions from manager
                const completedActions = campaignManager.getCompletedActions();

                // Update UI state to show completed actions
                setCampaignState(completedActions);
                setCampaignStateReceived(true);

                logger.logInfo(
                    'CampaignStateInitialized',
                    `Initialized with ${completedActions.size} completed actions`
                );
            } catch (error) {
                console.error('[LftSidePaneTrialCountdownCard] Error handling campaign state response:', error);
            }
        };

        // Listen for response event
        communication.addEventListener<CampaignStateResponsePayload>(
            ProgrammableSurfacesEvents.CampaignStateResponse,
            handleCampaignStateResponse
        );

        // Request campaign state from Win32
        logger.logInfo('RequestCampaignState', 'Requesting campaign state from Win32');
        campaignManager.requestCampaignState();

        // Cleanup on unmount
        return () => {
            communication.removeEventListener<CampaignStateResponsePayload>(
                ProgrammableSurfacesEvents.CampaignStateResponse,
                handleCampaignStateResponse
            );
        };
    }, [campaignManager]);

    // Recreate actions when campaignState updates to refresh onClick handlers
    const actions = React.useMemo(() => [
        {
            ...LftCountdownResearcherActionCard,
            onClick: () => handleActionClick(
                LftCountdownActionId.ResearcherDialog,
                'LaunchResearcherDialog',
                'User clicked on researcher card.'
            )
        },
        {
            ...LftCountdownBrainstormActionCard,
            onClick: () => handleActionClick(
                LftCountdownActionId.BrainStormDialog,
                'LaunchBrainstormDialog',
                'User clicked on brainstorm card.'
            )
        },
        {
            ...LftCountdownAnalystActionCard,
            onClick: () => handleActionClick(
                LftCountdownActionId.AnalystDialog,
                'LaunchAnalystDialog',
                'User clicked on analyst card.'
            )
        },
        {
            ...LftCountdownTryCopilotActionCard,
            onClick: () => handleActionClick(
                LftCountdownActionId.TryCopilotDialog,
                'LaunchTryCopilotDialog',
                'User clicked on Launch Try Copilot card.'
            )
        }
    ], [campaignState]);

    return (
        <div className={styles.container}>
            <LftCountdownCard daysLeft={7} actions={actions} />
        </div>
    );
};

export default LftTrialCountdownExperiment;
